import { registry } from '@/components-lib/registry'
import type { SceneObject } from '@/types/scene'
import { resolveUniformScaledParams } from '@/utils/uniformScaleParams'

const DEFAULT_BOUNDS = { w: 4, d: 4, h: 2 }
const STEP = 4
const GAP = 1

/** 与生成/吸附/阵列共用的 XZ 占地（米）；w≈X 向宽，d≈Z 向深 */
export function getBoundsFor(type: string, params: Record<string, any>): { w: number; d: number; h: number } {
  const def = registry.get(type)
  if (!def?.getBounds) return DEFAULT_BOUNDS
  const resolved = { ...params }
  for (const p of def.params) {
    if (resolved[p.key] === undefined) resolved[p.key] = p.default
  }
  return def.getBounds(resolveUniformScaledParams(def, resolved))
}

function overlapsXZ(
  x1: number, z1: number, w1: number, d1: number,
  x2: number, z2: number, w2: number, d2: number
): boolean {
  const halfW1 = w1 / 2 + GAP / 2
  const halfD1 = d1 / 2 + GAP / 2
  const halfW2 = w2 / 2 + GAP / 2
  const halfD2 = d2 / 2 + GAP / 2
  const overlapX = Math.abs(x1 - x2) < halfW1 + halfW2
  const overlapZ = Math.abs(z1 - z2) < halfD1 + halfD2
  return overlapX && overlapZ
}

/**
 * 在 XZ 平面找一个与现有物体不重叠的放置位置（用于点击面板创建新组件）。
 * 从 (0,0) 开始沿螺旋/网格尝试，步长 STEP，留出 GAP 间距。
 */
export function getSpawnPosition(
  type: string,
  params: Record<string, any>,
  existingObjects: SceneObject[],
  yElevation: number = 0
): [number, number, number] {
  const myBounds = getBoundsFor(type, params)

  const existing = existingObjects.map(obj => {
    const b = getBoundsFor(obj.type, obj.params)
    return { x: obj.position[0], z: obj.position[2], w: b.w, d: b.d }
  })

  const tryPosition = (x: number, z: number): boolean => {
    return !existing.some(
      e => overlapsXZ(x, z, myBounds.w, myBounds.d, e.x, e.z, e.w, e.d)
    )
  }

  if (tryPosition(0, 0)) return [0, yElevation, 0]

  let ring = 1
  const maxRings = 50
  while (ring <= maxRings) {
    const side = ring * 2
    let x = ring * STEP
    let z = -ring * STEP
    for (let i = 0; i < side; i++) {
      if (tryPosition(x, z)) return [x, yElevation, z]
      z += STEP
    }
    z = ring * STEP
    for (let i = 0; i < side; i++) {
      if (tryPosition(x, z)) return [x, yElevation, z]
      x -= STEP
    }
    x = -ring * STEP
    for (let i = 0; i < side; i++) {
      if (tryPosition(x, z)) return [x, yElevation, z]
      z -= STEP
    }
    z = -ring * STEP
    for (let i = 0; i < side; i++) {
      if (tryPosition(x, z)) return [x, yElevation, z]
      x += STEP
    }
    ring++
  }

  return [0, yElevation, 0]
}

/** 阵列等场景：取物体在 XZ 平面的外廓尺寸 */
export function getObjectFootprintXZ(obj: SceneObject): { w: number; d: number } {
  const b = getBoundsFor(obj.type, obj.params ?? {})
  return { w: Math.max(0.1, b.w), d: Math.max(0.1, b.d) }
}

/**
 * 从指定点 (preferredX, preferredZ) 起找与现有物体不重叠的放置位置（用于 Alt+点击复制）。
 * 先尝试点击位置，若重叠则沿该点向外螺旋搜索。
 */
export function getNonOverlappingPosition(
  preferredX: number,
  preferredZ: number,
  type: string,
  params: Record<string, any>,
  existingObjects: SceneObject[],
  yElevation: number = 0
): [number, number, number] | null {
  const myBounds = getBoundsFor(type, params)
  const existing = existingObjects.map(obj => {
    const b = getBoundsFor(obj.type, obj.params)
    return { x: obj.position[0], z: obj.position[2], w: b.w, d: b.d }
  })
  const tryPosition = (x: number, z: number): boolean => {
    return !existing.some(
      e => overlapsXZ(x, z, myBounds.w, myBounds.d, e.x, e.z, e.w, e.d)
    )
  }

  const px = Math.round(preferredX / STEP) * STEP
  const pz = Math.round(preferredZ / STEP) * STEP
  if (tryPosition(px, pz)) return [px, yElevation, pz]

  let ring = 1
  const maxRings = 50
  while (ring <= maxRings) {
    const side = ring * 2
    let x = px + ring * STEP
    let z = pz - ring * STEP
    for (let i = 0; i < side; i++) {
      if (tryPosition(x, z)) return [x, yElevation, z]
      z += STEP
    }
    z = pz + ring * STEP
    for (let i = 0; i < side; i++) {
      if (tryPosition(x, z)) return [x, yElevation, z]
      x -= STEP
    }
    x = px - ring * STEP
    for (let i = 0; i < side; i++) {
      if (tryPosition(x, z)) return [x, yElevation, z]
      z -= STEP
    }
    z = pz - ring * STEP
    for (let i = 0; i < side; i++) {
      if (tryPosition(x, z)) return [x, yElevation, z]
      x += STEP
    }
    ring++
  }
  return null
}
